<div class="mat-typography cross-tenant">
    <form *ngIf="form" [formGroup]="form">
        <div class="require-banner" *ngIf="missingTargetsLabels">
            <mat-icon>error_outline</mat-icon>
            <span>
                "Applies to" must target all users or at least one selected user or group for:
                {{ missingTargetsLabels }}.
            </span>
        </div>
        <!-- Declared inside the form so formControlName resolves to this form group. -->
        <ng-template #accessRows let-setting>
            <h4 class="setting-title">{{ setting.label }}</h4>
            <table>
                <tr>
                    <th>Field</th>
                    <th>Current Value</th>
                    <th>Desired Value</th>
                    <th>Status</th>
                </tr>

                <tr>
                    <td>Users and groups</td>
                    <td>{{ currentUsersValue(setting) }}</td>
                    <td class="desired-value">
                        <mat-form-field appearance="fill">
                            <mat-label>Access</mat-label>
                            <mat-select [formControlName]="setting.usersKey">
                                <mat-option *ngFor="let option of accessTypeOptions" [value]="option.value">
                                    {{ option.label }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </td>
                    <td>
                        <sway-baseline-status [hasError]="usersDeviated(setting)"></sway-baseline-status>
                    </td>
                </tr>

                <tr *ngIf="supportsTargets(setting)">
                    <td>Applies to</td>
                    <td>{{ currentAppliesToValue(setting) }}</td>
                    <td class="desired-value">
                        <mat-form-field appearance="fill">
                            <mat-label>Applies to</mat-label>
                            <mat-select [formControlName]="setting.appliesToKey">
                                <mat-option *ngFor="let option of appliesToOptions" [value]="option.value">
                                    {{ option.label }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>

                        <ul class="applies-to-picker" *ngIf="isSelectMode(setting)">
                            <li>
                                <span class="picker-label">
                                    Users <span class="number">({{ selectedUsersCount(setting) }})</span>
                                </span>
                                <button
                                    type="button"
                                    mat-button
                                    color="primary"
                                    [disabled]="isPickerLocked(setting, 'users')"
                                    (click)="manageItems(setting, 'users')">
                                    Manage
                                </button>
                            </li>
                            <li>
                                <span class="picker-label">
                                    Groups <span class="number">({{ selectedGroupsCount(setting) }})</span>
                                </span>
                                <button
                                    type="button"
                                    mat-button
                                    color="primary"
                                    [disabled]="isPickerLocked(setting, 'groups')"
                                    (click)="manageItems(setting, 'groups')">
                                    Manage
                                </button>
                            </li>
                        </ul>

                        <div class="selection-required-error" *ngIf="appliesToSelectionMissing(setting)">
                            At least one user or group is required.
                        </div>
                    </td>
                    <td>
                        <sway-baseline-status [hasError]="appliesToDeviated(setting)"></sway-baseline-status>
                    </td>
                </tr>

                <tr>
                    <td>Applications</td>
                    <td>{{ currentApplicationsValue(setting) }}</td>
                    <td class="desired-value">
                        <mat-form-field appearance="fill">
                            <mat-label>Access</mat-label>
                            <mat-select [formControlName]="setting.appsKey">
                                <mat-option
                                    *ngFor="let option of accessTypeOptions"
                                    [value]="option.value"
                                    [disabled]="(option.value === 'allowed' && isApplicationsLocked(setting)) || (option.value === 'blocked' && isBlockedAppsDisabled(setting))">
                                    {{ option.label }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                        <div class="lock-hint" *ngIf="isApplicationsLocked(setting)">
                            <mat-icon>info</mat-icon>
                            <span>Applications are blocked because users and groups are blocked.</span>
                        </div>
                        <div class="lock-hint" *ngIf="isBlockedAppsDisabled(setting)">
                            <mat-icon>info</mat-icon>
                            <span>Applications cannot be blocked because users and groups are allowed.</span>
                        </div>
                    </td>
                    <td>
                        <sway-baseline-status [hasError]="applicationsDeviated(setting)"></sway-baseline-status>
                    </td>
                </tr>
            </table>
        </ng-template>

        <mat-accordion multi>
            <!-- 1. Outbound access settings -->
            <mat-expansion-panel expanded>
                <mat-expansion-panel-header>
                    <mat-panel-title>Outbound access settings</mat-panel-title>
                </mat-expansion-panel-header>

                <ng-container *ngFor="let setting of outboundSettings">
                    <ng-container *ngTemplateOutlet="accessRows; context: { $implicit: setting }"></ng-container>
                </ng-container>
            </mat-expansion-panel>

            <!-- 2. Tenant restrictions -->
            <mat-expansion-panel>
                <mat-expansion-panel-header>
                    <mat-panel-title>Tenant restrictions</mat-panel-title>
                </mat-expansion-panel-header>

                <ng-container *ngFor="let setting of tenantRestrictionSettings">
                    <ng-container *ngTemplateOutlet="accessRows; context: { $implicit: setting }"></ng-container>
                </ng-container>
            </mat-expansion-panel>

            <!-- 3. Inbound access settings -->
            <mat-expansion-panel>
                <mat-expansion-panel-header>
                    <mat-panel-title>Inbound access settings</mat-panel-title>
                </mat-expansion-panel-header>

                <h4 class="setting-title">Trust settings</h4>
                <table>
                    <tr>
                        <th>Field</th>
                        <th>Current Value</th>
                        <th>Desired Value</th>
                        <th>Status</th>
                    </tr>

                    <tr>
                        <td>
                            <div class="item">
                                Trust inbound MFA
                                <mat-icon
                                    matTooltip="Trust multi-factor authentication claims from external Microsoft Entra organizations.">info</mat-icon>
                            </div>
                        </td>
                        <td>{{ isMfaAcceptedCurrentValue }}</td>
                        <td class="desired-value">
                            <mat-slide-toggle formControlName="isMfaAccepted">
                                {{ form.get('isMfaAccepted')?.value ? 'Trusted' : 'Not trusted' }}
                            </mat-slide-toggle>
                        </td>
                        <td>
                            <sway-baseline-status [hasError]="isMfaAcceptedDeviated"></sway-baseline-status>
                        </td>
                    </tr>

                    <tr>
                        <td>
                            <div class="item">
                                Trust inbound compliant devices
                                <mat-icon
                                    matTooltip="Trust compliant device claims from external Microsoft Entra organizations.">info</mat-icon>
                            </div>
                        </td>
                        <td>{{ isCompliantDeviceAcceptedCurrentValue }}</td>
                        <td class="desired-value">
                            <mat-slide-toggle formControlName="isCompliantDeviceAccepted">
                                {{ form.get('isCompliantDeviceAccepted')?.value ? 'Trusted' : 'Not trusted' }}
                            </mat-slide-toggle>
                        </td>
                        <td>
                            <sway-baseline-status [hasError]="isCompliantDeviceAcceptedDeviated"></sway-baseline-status>
                        </td>
                    </tr>

                    <tr>
                        <td>
                            <div class="item">
                                Trust inbound hybrid Azure AD joined devices
                                <mat-icon
                                    matTooltip="Trust hybrid Azure AD joined device claims from external Microsoft Entra organizations.">info</mat-icon>
                            </div>
                        </td>
                        <td>{{ isHybridAzureADJoinedDeviceAcceptedCurrentValue }}</td>
                        <td class="desired-value">
                            <mat-slide-toggle formControlName="isHybridAzureADJoinedDeviceAccepted">
                                {{ form.get('isHybridAzureADJoinedDeviceAccepted')?.value ? 'Trusted' : 'Not trusted' }}
                            </mat-slide-toggle>
                        </td>
                        <td>
                            <sway-baseline-status [hasError]="isHybridAzureADJoinedDeviceAcceptedDeviated"></sway-baseline-status>
                        </td>
                    </tr>
                </table>

                <ng-container *ngFor="let setting of inboundSettings">
                    <ng-container *ngTemplateOutlet="accessRows; context: { $implicit: setting }"></ng-container>
                </ng-container>
            </mat-expansion-panel>
        </mat-accordion>
    </form>
</div>
